import { PagedResult, panelDelete, panelGet, panelPost, panelPut } from "./panel.api";
import { ApproverListParams, ApproverPayload, ApproverRecord } from "./types";

/**
 * approver — /work-order/approver (Approval Management → Approvers tab)
 *
 * `authorityGroup` is the designation grouping the UI shows as sub-tabs
 * (MD/GCEO, GCFO/CFO/CAO/COO, Head of Subsidiary, APC).
 *
 * V4 re-homed these seven routes from `/approver/*` (and the parallel
 * `/spa/approver/*`) under `/work-order/approver/*`. Payloads and params are
 * byte-identical to V3 — only the prefix moved.
 */
const BASE = "/work-order/approver";
export const approverApi = {
  list: (params: ApproverListParams = {}) =>
    panelGet<PagedResult<ApproverRecord>>(`${BASE}/get-approver-list`, {
      page: 0,
      size: 20,
      orderBy: "userName",
      ...params,
    }),
  detail: (uniqueId: string) => panelGet<ApproverRecord>(`${BASE}/approver-detail`, { uniqueId }),
  /**
   * The approvers a work order should route to.
   *
   * This is the route to use while raising a work order — the Approvers tab's
   * paged `get-approver-list` is an administration listing and was only being
   * borrowed because nothing better was known. Params mirror what the chain is
   * scoped by; the caller still matches on subsidiary and cost centre so the
   * rows are right whether or not the server narrows them.
   */
  forWorkOrder: (params: {
    authorityGroup?: string;
    subsidiary?: string;
    division?: string;
    designation?: string;
    /** Required for the Head of Subsidiary R2 designation. */
    budget?: string;
  }) => panelGet<ApproverRecord[]>(`${BASE}/get-approver-for-spa`, params),

  /** Candidate users for a given authority group / org position. */
  candidates: (params: {
    authorityGroup?: string;
    subsidiary?: string;
    division?: string;
    designation?: string;
    budget?: string;
  }) => panelGet<ApproverRecord[]>(`${BASE}/get-user-list`, params),
  owners: () => panelGet<ApproverRecord[]>(`${BASE}/get-owner-user-list`),

  /** create takes an ARRAY — the form submits all its rows at once. */
  create: (payload: ApproverPayload[]) => panelPost(`${BASE}/create-approver`, payload),
  update: (payload: ApproverPayload) => panelPut(`${BASE}/update-approver`, payload),
  remove: (uniqueId: string) => panelDelete(`${BASE}/delete-approver`, { uniqueId }),
};
